/**
 * The table screen. Built once, then patched in place on every view — so the
 * CSS transforms on the cards animate instead of snapping.
 */

import {
  type Card,
  HAND_SIZE,
  type PlayerView,
  SUIT_NAMES,
  SUIT_SYMBOLS,
  type Seat,
  type Suit,
  explainReason,
  moveRulings,
} from '@manille/core';

import { cardBack, cardElement } from './card.js';
import { clear, el, initials, onLongPress } from './dom.js';
import { toast } from './toast.js';

export interface TableActions {
  play(card: Card): void;
  declare(trump: Suit | null): void;
  double(raise: boolean): void;
  nextDeal(): void;
  rematch(): void;
  leave(): void;
}

/**
 * The four trump buttons, left to right. Not `SUITS` order (♣ ♦ ♥ ♠), which
 * puts the two reds side by side — alternating the colours keeps each button
 * distinct from its neighbour at a glance.
 */
const TRUMP_PICK_ORDER = ['c', 'd', 's', 'h'] as const satisfies readonly Suit[];

/** Where a seat sits relative to me: I am always at the bottom. */
type Direction = 'bottom' | 'left' | 'top' | 'right';

const DIRECTIONS: readonly Direction[] = ['bottom', 'left', 'top', 'right'];

function directionOf(seat: Seat, mySeat: Seat): Direction {
  return DIRECTIONS[(seat - mySeat + 4) % 4] as Direction;
}

/** Offsets, in fractions of the trick box, for a card played from each side. */
const TRICK_OFFSET: Record<Direction, readonly [number, number]> = {
  bottom: [0, 0.13],
  left: [-0.15, -0.02],
  top: [0, -0.13],
  right: [0.15, 0.02],
};

const TRICK_ROTATION: Record<Direction, number> = {
  bottom: 0,
  left: -6,
  top: 3,
  right: 6,
};

/** Where a card flies in from — the direction of the player who played it. */
const ARRIVAL: Record<Direction, readonly [number, number]> = {
  bottom: [0, 1],
  left: [-1, 0],
  top: [0, -1],
  right: [1, 0],
};

/**
 * The one piece of fan geometry in the app.
 *
 * The grip point, as a fraction of the card. A real hand holds a fan by the
 * *bottom-left corner* of the stack, not the middle of the bottom edge — every
 * card rotates about that one shared point, which is why the bottoms converge
 * to a point while the tops sweep wide and each card shows a narrow sliver.
 *
 * Pivoting about the horizontal centre instead splays the bottoms outward and
 * produces a rosette, which is emphatically not a hand of cards.
 */
const FAN_PIVOT_X = 0.16;
const FAN_PIVOT_Y = 0.98;
/**
 * Radians per card ≈ 14°. Wider than a real hand needs, deliberately: the
 * exposed sliver of each card *is* its tap target, and a thumb needs about
 * 30 px of it.
 */
const FAN_STEP = 0.24;
/**
 * Past about 49° a card stops reading as a card and starts reading as a blank
 * rhombus, so the outermost one is never tilted further than this — the sweep
 * closes up instead.
 */
const FAN_MAX_TILT = 0.85;
/** Never collapse the fan into an untappable stack, whatever the measurements say. */
const FAN_MIN_STEP = 0.075;
/** How far a playable card slides out of the fan when it is your turn. */
const FAN_LIFT = 8;

function fanGeometry(count: number, width: number, height: number, available: number) {
  const mid = (count - 1) / 2;
  // The far top corner of the card, relative to the grip point.
  const armX = width * (1 - FAN_PIVOT_X);
  const armY = height * FAN_PIVOT_Y;

  // Clamp so the *readable* part stays on screen. Card bodies may run off the
  // edge — they are clipped by `.app` and nobody reads a covered card's middle
  // — but every rank index has to be visible, so the index arc is what bounds
  // the sweep. Bounding the far corner instead wastes most of the width.
  const indexRadius = Math.hypot(0.02 * width, 0.88 * height);
  // Half a card plus a thumb's worth of margin used to be reserved at each end;
  // it cost the fan a fifth of its sweep, and sweep is what separates the cards
  // enough to hit one. The outermost index now sits closer to the edge.
  const halfRoom = Math.max(0, available / 2 - 0.4 * width);
  const maxCorner = Math.asin(Math.min(1, indexRadius > 0 ? halfRoom / indexRadius : 1));
  const maxTilt = Math.max(0, maxCorner);
  // The floor matters more than the ceiling: if the clamp ever collapses to
  // zero the whole hand stacks into one pile, and then only the top card can be
  // tapped at all. A hand must always be separated enough to hit.
  const step =
    count > 1
      ? Math.max(
          FAN_MIN_STEP,
          Math.min(FAN_STEP, Math.min(maxTilt, FAN_MAX_TILT) / Math.max(mid, 0.5)),
        )
      : 0;

  // Where the fan actually lands: rotate all four corners of every card about
  // the grip point and take the extremes. Measuring this from the DOM instead
  // reads cards mid-transition and makes the fan drift about, so it is done in
  // closed form.
  const corners: readonly (readonly [number, number])[] = [
    [-FAN_PIVOT_X * width, -armY],
    [armX, -armY],
    [armX, (1 - FAN_PIVOT_Y) * height],
    [-FAN_PIVOT_X * width, (1 - FAN_PIVOT_Y) * height],
  ];

  // The only part of a covered card you can actually read is its rank index,
  // in the top-left corner. That arc of indices is what the eye treats as "the
  // hand", so the fan is centred on *it* rather than on the card bodies — which
  // shifts the whole thing right, and is what you want.
  const indexPoint: readonly [number, number] = [
    (0.14 - FAN_PIVOT_X) * width,
    -armY + 0.1 * height,
  ];

  let minIndex = Infinity;
  let maxIndex = -Infinity;
  let rise = 0; // highest point of any card above the grip
  for (let i = 0; i < count; i += 1) {
    const angle = (i - mid) * step;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    for (const [cx, cy] of corners) {
      rise = Math.max(rise, -(cx * sin + cy * cos));
    }
    const ix = indexPoint[0] * cos - indexPoint[1] * sin;
    minIndex = Math.min(minIndex, ix);
    maxIndex = Math.max(maxIndex, ix);
  }
  if (!Number.isFinite(minIndex)) {
    minIndex = 0;
    maxIndex = 0;
  }

  const centringShift = (0.5 - FAN_PIVOT_X) * width - (minIndex + maxIndex) / 2;

  return { step, mid, centringShift, rise, width, height };
}

/** Lay a set of nodes out around the shared grip point, inside `container`. */
function layoutFan(container: HTMLElement, nodes: readonly HTMLElement[]): void {
  const first = nodes[0];
  if (!first) return;
  const geometry = fanGeometry(
    nodes.length,
    first.offsetWidth || 64,
    first.offsetHeight || 94,
    container.clientWidth || 340,
  );
  nodes.forEach((node, index) => {
    applyFanTransform(node, index, geometry, 0);
    node.style.zIndex = String(10 + index);
  });
  reserveFanBox(
    container,
    first.offsetWidth || 64,
    first.offsetHeight || 94,
    container.clientWidth || 340,
  );
}

/**
 * Every card sits at the same spot and is rotated about the shared grip point;
 * `offsetAlongCard` slides one out along its own axis (the lift on your turn,
 * the drop on an illegal card).
 */
function applyFanTransform(
  node: HTMLElement,
  index: number,
  geometry: ReturnType<typeof fanGeometry>,
  offsetAlongCard: number,
  shift = 0,
): string {
  const angle = (index - geometry.mid) * geometry.step;
  node.style.transformOrigin = `${(FAN_PIVOT_X * 100).toFixed(1)}% ${(FAN_PIVOT_Y * 100).toFixed(1)}%`;
  const transform =
    `translateX(calc(-50% + ${(geometry.centringShift + shift).toFixed(1)}px)) ` +
    `rotate(${angle.toFixed(4)}rad) translateY(${offsetAlongCard}px)`;
  node.style.transform = transform;
  return transform;
}

/**
 * Reserve the height a *full* hand reaches above the container's bottom edge.
 * Measuring the hand you currently hold would shrink the box as you play, and
 * everything stacked above it — your chip, the prompt, the whole table — would
 * creep downward trick by trick. The reservation is the same from the first
 * card to the last.
 */
function reserveFanBox(
  container: HTMLElement,
  width: number,
  height: number,
  available: number,
): void {
  applyFanBox(container, fanGeometry(HAND_SIZE, width, height, available));
}

function applyFanBox(container: HTMLElement, geometry: ReturnType<typeof fanGeometry>): void {
  const reveal = Number(
    getComputedStyle(container).getPropertyValue('--hand-reveal').trim() || '1',
  );
  const gripAboveBottom = (reveal - FAN_PIVOT_Y) * geometry.height;
  // The lift is reserved on every frame, not only on your turn: measuring it
  // conditionally makes the whole hand — and your avatar above it — hop by 8px
  // every time the turn comes round.
  const height = Math.max(0, gripAboveBottom + geometry.rise + FAN_LIFT);
  container.style.setProperty('--fan-height', `${height.toFixed(1)}px`);
  // The felt above stops where the hand layer starts; publish that height so
  // the table can size itself against it.
  const zone = container.parentElement;
  if (zone) {
    document.documentElement.style.setProperty('--hand-h', `${zone.offsetHeight}px`);
  }
}

export interface TableScreen {
  readonly root: HTMLElement;
  update(view: PlayerView): void;
  destroy(): void;
}

export function mountTable(actions: TableActions): TableScreen {
  const root = el('div', 'app__screen table-screen');
  root.style.display = 'contents';

  // ── header ──────────────────────────────────────────────────────────────
  const header = el('header', 'header');
  const trumpChip = el('div', 'trump');
  const trumpSymbol = el('div', 'trump__symbol', '—');
  const trumpLabel = el('div', 'trump__label', 'Troef');
  trumpChip.append(trumpSymbol, trumpLabel);

  // Broadcast scoreboard: a coloured team block at each end, the *running
  // deal* score in the dark middle — that is the number that moves every
  // trick, the one worth watching live. The match total only changes once a
  // deal ends, so it does not deserve a permanent slot fighting for the same
  // header row; tapping the bar opens the full round-by-round history
  // instead, with the match total as its summary line.
  const scorebar = el('button', 'scorebar');
  scorebar.type = 'button';
  scorebar.title = 'Scoreverloop';
  scorebar.setAttribute('aria-label', 'Bekijk scoreverloop');
  const dealUs = el('b', 'scorebar__num', '0');
  const dealThem = el('b', 'scorebar__num', '0');

  const sideUs = el('span', 'scorebar__side');
  sideUs.dataset['team'] = 'us';
  sideUs.append(el('i', 'scorebar__tag', 'WIJ'), dealUs);

  const sideThem = el('span', 'scorebar__side');
  sideThem.dataset['team'] = 'them';
  sideThem.append(dealThem, el('i', 'scorebar__tag', 'ZIJ'));

  scorebar.append(sideUs, el('i', 'scorebar__rule'), sideThem);

  // Without this there is no way out of a table: the app remembers where you
  // were and rejoins it on every launch.
  const leaveButton = el('button', 'leave-button', '✕');
  leaveButton.title = 'Tafel verlaten';
  leaveButton.setAttribute('aria-label', 'Tafel verlaten');
  leaveButton.addEventListener('click', () => {
    if (confirm('Tafel verlaten? Een bot neemt je hand over.')) actions.leave();
  });

  const headerTop = el('div', 'header__top');
  headerTop.append(trumpChip, scorebar, leaveButton);
  header.append(headerTop);

  // ── table ───────────────────────────────────────────────────────────────
  const table = el('main', 'table');
  const seatNodes = new Map<Direction, SeatChip>();
  for (const direction of ['left', 'top', 'right'] as const) {
    const chip = seatChip(direction);
    seatNodes.set(direction, chip);
    table.append(chip.root);
  }
  const trickBox = el('div', 'trick');
  const banner = el('div', 'banner');
  banner.hidden = true;
  // Long-press on the felt is the primary gesture, but it is invisible — and
  // impossible to discover with a mouse. This is the same action with a target
  // you can see; it only appears once there is a trick worth looking at.
  const reviewButton = el('button', 'review-button');
  reviewButton.type = 'button';
  reviewButton.title = 'Vorige slag';
  reviewButton.setAttribute('aria-label', 'Bekijk de vorige slag');
  reviewButton.hidden = true;
  // Two cards, not an arrow: the button shows cards, and a stack reads as one
  // shape at 19px where a circular arrow was all tail and gap. The front card
  // is filled with the button's own background so the outlines never cross.
  reviewButton.innerHTML = `
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"
         stroke-linejoin="round" aria-hidden="true">
      <rect x="4.5" y="5" width="10" height="14" rx="2" transform="rotate(-20 9.5 12)" />
      <rect x="11" y="5" width="10" height="14" rx="2" fill="var(--review-bg)" />
      <path d="M16 10.3l1.7 1.9-1.7 1.9-1.7-1.9z" fill="currentColor" stroke="none" />
    </svg>`;
  // Decisions — naming trump, kloppen — are asked in the middle of the table,
  // where they are unmissable, rather than squeezed in above the hand.
  const prompt = el('div', 'prompt');
  prompt.hidden = true;
  table.append(trickBox, banner, prompt);

  // ── hand ────────────────────────────────────────────────────────────────
  const handZone = el('div', 'hand-zone');
  const self = el('div', 'self');
  const selfAvatar = el('div', 'avatar');
  const selfName = el('div', 'seat__name', 'Jij');
  // Your own seat needs the dealer button too — without it, the one case where
  // nobody's avatar is marked is the case where the dealer is you.
  const selfDealer = el('div', 'seat__dealer', 'D');
  selfDealer.title = 'Jij deelt';
  selfDealer.hidden = true;
  selfAvatar.append(selfDealer);
  self.append(selfAvatar, selfName);
  const hand = el('div', 'hand');
  const actionBar = el('div', 'actions');
  // The hand comes last and bleeds off the bottom of the screen, so anything
  // you need to tap has to sit above it. Status and decisions go *above* the
  // player chip, in the open felt between the trick and your name — hung
  // underneath the chip they read as a caption on your own avatar rather than
  // as what the table is telling you.
  handZone.append(actionBar, self, hand);

  // A sibling of the table and the hand, not a child of the table: the hand
  // layer paints over the whole table layer, and the outermost card of the fan
  // reaches far enough up the right edge to swallow every tap on the button.
  root.append(header, table, handZone, reviewButton);

  // ── interaction ─────────────────────────────────────────────────────────
  const cardNodes = new Map<Card, HTMLElement>();
  // Kept across frames so a card animates once, when it is actually played,
  // instead of every time a new view arrives.
  const trickNodes = new Map<Card, HTMLElement>();
  let latest: PlayerView | null = null;
  let lastScoreKey = '';

  // Three independent slots. The review and history overlays are opened by
  // the player and only they close them; the score overlay is driven by the
  // phase. Sharing one slot meant an incoming frame could rip an overlay away
  // mid-read.
  let reviewNode: HTMLElement | null = null;
  let scoreNode: HTMLElement | null = null;
  let historyNode: HTMLElement | null = null;

  function host(): HTMLElement {
    return root.parentElement ?? document.body;
  }

  function closeReview(): void {
    reviewNode?.remove();
    reviewNode = null;
  }

  function closeScore(): void {
    scoreNode?.remove();
    scoreNode = null;
  }

  function closeHistory(): void {
    historyNode?.remove();
    historyNode = null;
  }

  const showLastTrick = (): void => {
    if (!latest?.lastTrick) {
      toast('Nog geen slag om te bekijken.');
      return;
    }
    if (reviewNode) return;
    reviewNode = reviewOverlay(latest, closeReview);
    host().append(reviewNode);
  };
  onLongPress(table, showLastTrick);
  reviewButton.addEventListener('click', showLastTrick);

  scorebar.addEventListener('click', () => {
    if (!latest || historyNode) return;
    historyNode = historyOverlay(latest, closeHistory);
    host().append(historyNode);
  });

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      closeReview();
      closeHistory();
    }
  };
  document.addEventListener('keydown', onKeyDown);

  /**
   * `clientWidth` reads as 0 — or as something far too small — if the hand is
   * measured before layout has settled. Feeding that to the fan collapsed it
   * into an untappable stack, so never trust a width narrower than the window.
   */
  function handWidth(): number {
    const measured = hand.clientWidth;
    if (measured > 200) return measured;
    return Math.max(320, Math.min(window.innerWidth || 360, 560));
  }

  // A collapsed or mis-measured fan used to stay broken until the next state
  // change, which never comes while the table is waiting on you.
  const onResize = (): void => {
    if (latest) updateHand(latest);
  };
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);

  function tryPlay(card: Card, node: HTMLElement | null): void {
    if (!latest) return;
    const reason = illegalFor(latest, card);
    if (reason) {
      // Zero-latency refusal, computed from the exact module the server uses.
      toast(reason, 'warn');
      node?.classList.remove('card--nudge');
      void node?.offsetWidth;
      node?.classList.add('card--nudge');
      return;
    }
    actions.play(card);
  }

  hand.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('.card');
    const card = target?.dataset['card'] as Card | undefined;
    if (!card) return;
    tryPlay(card, target);
  });

  function update(view: PlayerView): void {
    latest = view;
    const mySeat = view.you.seat ?? 0;

    updateHeader(view);
    // The felt layer hangs off the header's height; publish it rather than
    // hard-coding a number that the safe-area inset would make wrong anyway.
    document.documentElement.style.setProperty('--header-h', `${header.offsetHeight}px`);
    updateSeats(view, mySeat);
    updateTrick(view, mySeat);
    updateSelf(view, mySeat);
    updateHand(view);
    updateActions(view);

    if (view.phase === 'deal-end' || view.phase === 'match-end') {
      const key = `${view.phase}:${view.dealNumber}:${view.version}`;
      // Rebuild only when the content changed. The overlay is never closed by
      // its own buttons — it goes away when the phase actually moves on, so a
      // rejected `next-deal` cannot leave the table with no way forward.
      if (!scoreNode || key !== lastScoreKey) {
        lastScoreKey = key;
        closeReview();
        closeScore();
        closeHistory();
        scoreNode = scoreOverlay(view, actions);
        host().append(scoreNode);
      }
    } else {
      lastScoreKey = '';
      closeScore();
    }
  }

  function updateHeader(view: PlayerView): void {
    const trump = view.trump;
    trumpSymbol.textContent = view.declaration
      ? trump === null
        ? 'NT'
        : SUIT_SYMBOLS[trump]
      : '—';
    trumpSymbol.dataset['red'] = String(trump === 'h' || trump === 'd');
    trumpLabel.textContent =
      view.declaration && trump === null
        ? 'Zonder troef'
        : view.declaration
          ? 'Troef'
          : 'Nog niet gekozen';

    const myTeam = view.you.team ?? 0;
    const theirTeam = myTeam === 0 ? 1 : 0;

    const us = view.dealPoints[myTeam] ?? 0;
    const them = view.dealPoints[theirTeam] ?? 0;
    dealUs.textContent = String(us);
    dealThem.textContent = String(them);
    // The bright number is the one in front — see the scorebar rules in the
    // stylesheet for why the team colours do not live here.
    scorebar.dataset['lead'] = us === them ? 'tie' : us > them ? 'us' : 'them';
  }

  function updateSeats(view: PlayerView, mySeat: Seat): void {
    for (const seat of view.seats) {
      if (seat.seat === mySeat) continue;
      const chip = seatNodes.get(directionOf(seat.seat, mySeat));
      if (!chip) continue;
      const mine = seat.team === view.you.team;
      chip.name.textContent = seat.name || '—';
      chip.avatar.textContent = initials(seat.name || '?');
      chip.avatar.dataset['team'] = mine ? 'us' : 'them';
      // Team colour on the chip and on the backs of their cards — you should
      // never have to work out who you are playing with.
      chip.root.dataset['team'] = mine ? 'us' : 'them';
      // Only the partner is labelled — if one seat says "partner", the other
      // two obviously are not. Bots carry the gold dot instead of a word.
      chip.tag.textContent = mine ? 'maat' : '';
      chip.avatar.title = seat.kind === 'bot' ? 'Gespeeld door een bot' : seat.name;
      chip.avatar.classList.toggle('avatar--bot', seat.kind === 'bot');
      chip.avatar.classList.toggle('avatar--turn', view.pendingSeat === seat.seat);
      chip.root.classList.toggle('seat--offline', seat.kind === 'human' && !seat.connected);
      chip.dealer.hidden = view.dealer !== seat.seat;
      syncBacks(chip.backs, seat.cardCount);
    }
  }

  function updateTrick(view: PlayerView, mySeat: Seat): void {
    reviewButton.hidden = view.lastTrick === null;

    const onTable = new Set(view.trick.plays.map((play) => play.card));
    const swept: HTMLElement[] = [];
    for (const [card, node] of trickNodes) {
      if (!onTable.has(card)) {
        swept.push(node);
        trickNodes.delete(card);
      }
    }
    if (swept.length > 0) {
      // The trick is *taken*: the cards slide off toward whoever won it rather
      // than blinking out of existence.
      const winner = view.lastTrick?.winner;
      const [ax, ay] = ARRIVAL[winner === undefined ? 'top' : directionOf(winner, mySeat)];
      const distance = Math.max(table.clientWidth, table.clientHeight) * 0.55;
      swept.forEach((node, index) => {
        node.classList.add('card--swept');
        node.style.transform =
          `translate(${(ax * distance).toFixed(0)}px, ${(ay * distance).toFixed(0)}px) ` +
          `rotate(${(ax * 22 + 6).toFixed(0)}deg) scale(0.82)`;
        node.style.opacity = '0';
        // A slight stagger reads as a hand gathering them up.
        window.setTimeout(() => node.remove(), 460 + index * 30);
      });
    }

    // Offsets are fractions of the trick box — of its *smaller* dimension, so a
    // wide desktop box does not fling the top card into the partner's chip.
    const box = Math.min(trickBox.clientWidth || 300, trickBox.clientHeight || 300);
    const reach = Math.max(table.clientWidth, table.clientHeight) * 0.62;

    for (const play of view.trick.plays) {
      const direction = directionOf(play.seat, mySeat);
      const [dx, dy] = TRICK_OFFSET[direction];
      let node = trickNodes.get(play.card);
      if (!node) {
        // New card: fly it in from the side its player is actually sitting on.
        node = cardElement(play.card);
        const [ax, ay] = ARRIVAL[direction];
        node.style.setProperty('--rot', `${TRICK_ROTATION[direction]}deg`);
        node.style.setProperty('--from-x', `${ax * reach}px`);
        node.style.setProperty('--from-y', `${ay * reach}px`);
        node.style.transform = `translate(${dx * box}px, ${dy * box}px) rotate(${TRICK_ROTATION[direction]}deg)`;
        trickNodes.set(play.card, node);
        trickBox.append(node);
      } else {
        node.style.transform = `translate(${dx * box}px, ${dy * box}px) rotate(${TRICK_ROTATION[direction]}deg)`;
      }
      const seat = view.seats.find((s) => s.seat === play.seat);
      node.dataset['team'] = seat && seat.team === view.you.team ? 'us' : 'them';
      node.classList.toggle(
        'card--winning',
        view.trickComplete && view.trickWinner === play.seat,
      );
    }
  }

  function updateSelf(view: PlayerView, mySeat: Seat): void {
    const me = view.seats.find((s) => s.seat === mySeat);
    selfName.textContent = me?.name || 'Jij';
    selfAvatar.textContent = initials(me?.name || 'Jij');
    selfAvatar.dataset['team'] = 'us';
    self.dataset['team'] = 'us';
    selfAvatar.classList.toggle('avatar--turn', view.pendingSeat === view.you.seat);
  }

  function updateHand(view: PlayerView): void {
    const cards = view.you.hand;
    const wanted = new Set(cards);
    for (const [card, node] of cardNodes) {
      if (!wanted.has(card)) {
        node.remove();
        cardNodes.delete(card);
      }
    }
    if (view.you.handHidden) {
      // Nobody touches their cards until the dealer has named trump. Laid out
      // with the very same arc as the face-up hand, so nothing jumps when the
      // cards turn over.
      const wanted = view.seats.find((s) => s.seat === view.you.seat)?.cardCount ?? 8;
      const backs = [...hand.querySelectorAll<HTMLElement>('.card-back')];
      while (backs.length > wanted) backs.pop()?.remove();
      while (backs.length < wanted) {
        const back = cardBack();
        hand.append(back);
        backs.push(back);
      }
      layoutFan(hand, backs);
      return;
    }
    for (const back of hand.querySelectorAll('.card-back')) back.remove();

    const rulings = view.turnSeat === view.you.seat && view.you.seat !== null
      ? moveRulings(cards, view.trick, view.trump, view.variant)
      : cards.map((card) => ({ card, legal: true as const }));
    const legality = new Map(rulings.map((r) => [r.card, r.legal]));

    const count = cards.length;
    for (const card of cards) {
      if (cardNodes.has(card)) continue;
      const node = cardElement(card);
      cardNodes.set(card, node);
      hand.append(node);
    }

    const sample = cardNodes.values().next().value as HTMLElement | undefined;
    const geometry = fanGeometry(
      count,
      sample?.offsetWidth || 64,
      sample?.offsetHeight || 94,
      handWidth(),
    );

    const isMyTurn = view.turnSeat === view.you.seat;
    // Illegal cards sit lower and lose their colour; legal ones lift when it is
    // actually your turn. Both move along the card's own axis, so the card
    // slides out of the fan rather than sideways off it.
    const offsetOf = (index: number): number => {
      const card = cards[index] as Card;
      const illegal = isMyTurn && legality.get(card) === false;
      return illegal ? 18 : isMyTurn ? -FAN_LIFT : 0;
    };

    cards.forEach((card, index) => {
      const node = cardNodes.get(card) as HTMLElement;
      applyFanTransform(node, index, geometry, offsetOf(index));
      node.style.zIndex = String(10 + index);
      node.classList.toggle('card--illegal', isMyTurn && legality.get(card) === false);
    });

    reserveFanBox(
      hand,
      sample?.offsetWidth || 64,
      sample?.offsetHeight || 94,
      handWidth(),
    );
    for (const card of cards) {
      const node = cardNodes.get(card) as HTMLElement;
      node.style.setProperty('--rest-transform', node.style.transform);
    }
  }

  /**
   * The server's clock, drawn. A CSS animation rather than a ticking interval:
   * the duration comes straight from the server's own remaining time, so every
   * incoming frame re-syncs the bar instead of letting a local counter drift.
   */
  function timerBar(view: PlayerView): HTMLElement | null {
    const left = view.deadlineIn;
    if (left === null || left === undefined) return null;
    const bar = el('div', 'prompt__timer');
    bar.style.animationDuration = `${Math.max(0, left)}ms`;
    return bar;
  }

  function updateActions(view: PlayerView): void {
    clear(actionBar);
    clear(prompt);
    const iAmDealer = view.you.seat !== null && view.you.seat === view.dealer;

    if (view.phase === 'declaring') {
      if (iAmDealer) {
        prompt.hidden = false;
        prompt.append(el('div', 'prompt__title', 'Kies troef'));
        const grid = el('div', 'suit-grid');
        for (const suit of TRUMP_PICK_ORDER) {
          const button = el('button', 'btn btn--suit', SUIT_SYMBOLS[suit]);
          button.dataset['suit'] = suit;
          button.setAttribute('aria-label', SUIT_NAMES[suit]);
          button.addEventListener('click', () => actions.declare(suit));
          grid.append(button);
        }
        prompt.append(grid);
        if (view.allowNoTrump) {
          const noTrump = el('button', 'btn btn--ghost', 'Zonder troef (×2)');
          noTrump.addEventListener('click', () => actions.declare(null));
          prompt.append(noTrump);
        }
        const clock = timerBar(view);
        if (clock) prompt.append(clock);
      } else {
        prompt.hidden = true;
        actionBar.append(waitingFor(view, 'kiest troef'));
      }
      return;
    }

    if (view.phase === 'doubling') {
      const mine = view.doubling && view.doubling.seat === view.you.seat;
      if (mine && view.doubling) {
        const label = view.doubling.stage === 'mee' ? 'Kloppen' : 'Tegenkloppen';
        prompt.hidden = false;
        prompt.append(
          el('div', 'prompt__title', `${label}?`),
          el(
            'div',
            'prompt__sub',
            `Inzet gaat naar ×${Math.min(view.multiplier * 2, 4 * view.carryMultiplier)}`,
          ),
        );
        const row = el('div', 'prompt__row');
        const pass = el('button', 'btn', 'Passen');
        pass.addEventListener('click', () => actions.double(false));
        const raise = el('button', 'btn btn--primary', label);
        raise.addEventListener('click', () => actions.double(true));
        row.append(pass, raise);
        prompt.append(row);
        const clock = timerBar(view);
        if (clock) prompt.append(clock);
      } else {
        prompt.hidden = true;
        actionBar.append(waitingFor(view, 'beslist over de inzet'));
      }
      return;
    }

    prompt.hidden = true;

    if (view.phase === 'playing') {
      const waiting =
        view.turnSeat !== null && view.turnSeat !== view.you.seat
          ? nameOf(view, view.turnSeat)
          : null;
      actionBar.append(
        el(
          'div',
          'actions__prompt',
          view.trickComplete
            ? `${nameOf(view, view.trickWinner ?? 0)} pakt de slag`
            : waiting
              ? `${waiting} is aan zet`
              : 'Jouw beurt',
        ),
      );
    }
  }

  function waitingFor(view: PlayerView, what: string): HTMLElement {
    const seat = view.pendingSeat;
    return el('div', 'actions__prompt', `${seat === null ? 'Iemand' : nameOf(view, seat)} ${what}…`);
  }

  return {
    root,
    update,
    destroy: () => {
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      closeReview();
      closeScore();
      closeHistory();
      root.remove();
    },
  };
}

// ── pieces ────────────────────────────────────────────────────────────────

interface SeatChip {
  root: HTMLElement;
  avatar: HTMLElement;
  name: HTMLElement;
  dealer: HTMLElement;
  backs: HTMLElement;
  tag: HTMLElement;
}

function seatChip(direction: Direction): SeatChip {
  const root = el('div', `seat seat--${direction}`);
  const avatar = el('div', 'avatar');
  const name = el('div', 'seat__name');
  // No card-count badge: the fan of backs beside the seat already shows how
  // many cards are left, and the number next to the name was competing with
  // it to say the same thing.
  const dealer = el('div', 'seat__dealer', 'D');
  dealer.title = 'Deler';
  dealer.hidden = true;
  // Card backs exist for the desktop layout only; in portrait CSS hides them,
  // because a fan of backs is exactly what gets squeezed on a phone.
  const backs = el('div', 'seat__backs');
  const tag = el('div', 'seat__tag');
  const head = el('div', 'seat__head');
  // Absolutely positioned, so it hangs off the avatar's edge rather than
  // sitting in the row as another item — a dealer button belongs on the felt
  // beside the player, not on their nameplate.
  head.append(avatar, name, dealer);
  root.append(head, tag, backs);
  return { root, avatar, name, dealer, backs, tag };
}

/**
 * The opponents' hands, face down. Spacing is derived from the container the
 * stylesheet gives us, so the same code produces a tight little stack on a
 * phone and a proper fan on a desktop.
 */
function syncBacks(container: HTMLElement, count: number): void {
  while (container.childElementCount > count) container.lastElementChild?.remove();
  while (container.childElementCount < count) container.append(cardBack());

  // Exactly the same arc as your own hand, just smaller.
  layoutFan(container, [...container.children] as HTMLElement[]);
}



function nameOf(view: PlayerView, seat: Seat): string {
  return view.seats.find((s) => s.seat === seat)?.name || `Stoel ${seat + 1}`;
}

function illegalFor(view: PlayerView, card: Card): string | null {
  if (view.phase !== 'playing') return 'Er wordt nog niet gespeeld.';
  if (view.turnSeat !== view.you.seat) return 'Niet jouw beurt.';
  const ruling = moveRulings(view.you.hand, view.trick, view.trump, view.variant).find(
    (r) => r.card === card,
  );
  if (!ruling || ruling.legal) return null;
  return ruling.reason ? explainReason(ruling.reason) : 'Die kaart mag niet.';
}

/** Long-press review: the four cards of the last trick and who took it. */
function reviewOverlay(view: PlayerView, close: () => void): HTMLElement {
  const overlay = el('div', 'overlay');
  // The long-press fires while the finger is still down, so the pointerup that
  // follows would land a click straight onto this overlay and shut it again.
  const openedAt = performance.now();
  const guarded = (run: () => void) => () => {
    if (performance.now() - openedAt > 350) run();
  };

  const card = el('div', 'overlay__card');
  const trick = view.lastTrick;

  const dismiss = el('button', 'overlay__close', '✕');
  dismiss.setAttribute('aria-label', 'Sluiten');
  dismiss.addEventListener('click', guarded(close));
  card.append(dismiss);

  card.append(el('h2', 'panel__title', `Slag ${trick?.number ?? ''}`));

  const grid = el('div', 'review');
  for (const play of trick?.plays ?? []) {
    const slot = el('div', 'review__slot');
    slot.dataset['winner'] = String(trick?.winner === play.seat);
    slot.append(cardElement(play.card), el('div', 'review__who', nameOf(view, play.seat)));
    grid.append(slot);
  }
  card.append(grid);
  card.append(
    el(
      'div',
      'panel__sub',
      trick ? `${nameOf(view, trick.winner)} pakt hem — ${trick.points} punten.` : '',
    ),
  );

  const done = el('button', 'btn btn--primary', 'Sluiten');
  done.addEventListener('click', guarded(close));
  card.append(done);
  overlay.append(card);
  // Tapping anywhere off the panel closes it too — nobody hunts for a button
  // mid-argument about who took the last trick.
  overlay.addEventListener(
    'click',
    guarded(() => close()),
  );
  return overlay;
}

function historyOverlay(view: PlayerView, close: () => void): HTMLElement {
  const overlay = el('div', 'overlay');
  const openedAt = performance.now();
  const guarded = (run: () => void) => () => {
    if (performance.now() - openedAt > 350) run();
  };

  const myTeam = view.you.team ?? 0;
  const theirTeam = myTeam === 0 ? 1 : 0;

  const card = el('div', 'overlay__card');
  const dismiss = el('button', 'overlay__close', '✕');
  dismiss.setAttribute('aria-label', 'Sluiten');
  dismiss.addEventListener('click', guarded(close));
  card.append(dismiss);
  card.append(el('h2', 'panel__title', 'Scoreverloop'));

  // The match score is what the panel is *for* — it decides the game, while
  // the round table underneath only explains how it got there. It used to be
  // one line of grey footnote below the rows, which had the hierarchy exactly
  // upside down. Same convention as the header scorebar: the bright number is
  // the side in front.
  const mine = view.matchPoints[myTeam] ?? 0;
  const theirs = view.matchPoints[theirTeam] ?? 0;
  const stand = el('div', 'stand');
  stand.dataset['lead'] = mine === theirs ? 'tie' : mine > theirs ? 'us' : 'them';
  const standUs = el('span', 'stand__side');
  standUs.dataset['team'] = 'us';
  standUs.append(el('i', 'stand__tag', 'WIJ'), el('b', 'stand__num', String(mine)));
  const standThem = el('span', 'stand__side');
  standThem.dataset['team'] = 'them';
  standThem.append(el('b', 'stand__num', String(theirs)), el('i', 'stand__tag', 'ZIJ'));
  stand.append(standUs, el('i', 'stand__rule'), standThem);
  card.append(stand, el('div', 'stand__goal', `naar ${view.target}`));

  const list = el('div', 'history');
  const head = el('div', 'history__row history__row--head');
  head.append(el('span', 'history__round', '#'));
  const headUs = el('b', 'history__pts', 'Wij');
  headUs.dataset['team'] = 'us';
  const headThem = el('b', 'history__pts', 'Zij');
  headThem.dataset['team'] = 'them';
  head.append(headUs, headThem, el('i', 'history__flag', ''));
  list.append(head);

  for (const { dealNumber, score } of view.dealHistory) {
    const row = el('div', 'history__row');
    row.append(el('span', 'history__round', String(dealNumber)));
    const us = el('b', 'history__pts', String(score.award[myTeam]));
    us.dataset['team'] = 'us';
    const them = el('b', 'history__pts', String(score.award[theirTeam]));
    them.dataset['team'] = 'them';
    row.append(us, them);
    if (score.plat) row.append(el('i', 'history__flag', 'plat'));
    list.append(row);
  }

  // The deal in progress has no `dealHistory` entry yet — it only gets one
  // once it is scored — so show it as its own trailing, unfinished row.
  if (view.dealNumber > view.dealHistory.length) {
    const row = el('div', 'history__row');
    row.dataset['live'] = 'true';
    row.append(el('span', 'history__round', String(view.dealNumber)));
    const us = el('b', 'history__pts', String(view.dealPoints[myTeam]));
    us.dataset['team'] = 'us';
    const them = el('b', 'history__pts', String(view.dealPoints[theirTeam]));
    them.dataset['team'] = 'them';
    row.append(us, them);
    row.append(el('i', 'history__flag', 'bezig'));
    list.append(row);
  }

  if (list.children.length <= 1) {
    card.append(el('div', 'panel__sub', 'Nog geen rondes gespeeld.'));
  } else {
    card.append(list);
  }

  const done = el('button', 'btn btn--primary', 'Sluiten');
  done.addEventListener('click', guarded(close));
  card.append(done);
  overlay.append(card);
  overlay.addEventListener(
    'click',
    guarded(() => close()),
  );
  return overlay;
}

function scoreOverlay(view: PlayerView, actions: TableActions): HTMLElement {
  const overlay = el('div', 'overlay');
  const panel = el('div', 'overlay__card');
  const score = view.lastDeal;
  const myTeam = view.you.team ?? 0;
  const theirTeam = myTeam === 0 ? 1 : 0;

  const won = view.phase === 'match-end' ? view.winnerTeam === myTeam : score?.winner === myTeam;
  panel.append(
    el(
      'h2',
      'panel__title',
      view.phase === 'match-end'
        ? won
          ? 'Partij gewonnen'
          : 'Partij verloren'
        : score?.plat
          ? 'Plat — 30 om 30'
          : won
            ? 'Ronde gewonnen'
            : 'Ronde verloren',
    ),
  );

  // The award is the news. The card split is the small print that explains it.
  if (score) {
    const scorer = score.winner;
    const awarded = scorer === null ? 0 : score.award[scorer];

    const hero = el('div', 'result');
    hero.dataset['team'] = scorer === null ? 'none' : scorer === myTeam ? 'us' : 'them';
    hero.append(
      el('div', 'result__points', scorer === null ? '0' : `+${awarded}`),
      el(
        'div',
        'result__who',
        scorer === null ? 'niemand scoort' : scorer === myTeam ? 'voor ons' : 'voor hen',
      ),
    );
    panel.append(hero);

    // Just the card split, plus whatever made this deal unusual. The full
    // derivation ("2 boven 30 · ×4") was arithmetic nobody reads on a result
    // screen — the running total is one tap away in the history popup.
    const steps: string[] = [`${score.teamPoints[myTeam]}–${score.teamPoints[theirTeam]}`];
    if (score.capot !== null) steps.push('capot');
    if (score.multiplier > 1) steps.push(`×${score.multiplier}`);
    if (score.plat && score.carryMultiplier > 1) steps.push('volgende ronde dubbel');
    panel.append(el('div', 'result__maths', steps.join(' · ')));
  }

  // Only at match end, where the final standing is the point. Between rounds
  // it was a third line saying what the header and the history popup already
  // say — and the running total is not what you want after a single deal.
  if (view.phase === 'match-end') {
    panel.append(
      el(
        'div',
        'code__hint',
        `${view.matchPoints[myTeam]} – ${view.matchPoints[theirTeam]} · naar ${view.target}`,
      ),
    );
  }

  if (view.phase === 'match-end') {
    if (view.you.isHost) {
      const again = el('button', 'btn btn--primary', 'Revanche');
      again.addEventListener('click', () => actions.rematch());
      panel.append(again);
    } else {
      panel.append(el('div', 'panel__sub', 'Wachten tot de host een revanche start…'));
    }
  } else {
    const next = el('button', 'btn btn--primary', 'Volgende ronde');
    next.addEventListener('click', () => actions.nextDeal());
    panel.append(next);
  }

  overlay.append(panel);
  return overlay;
}

